import { useMemo } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { DependencySource } from '../../../../shared/vendoredDependencies';
import { useIsFeatureEnabled } from '../../../helpers/features';
import type { DependencyQueryWithFilters } from '../../../../shared/dependenciesV2';

import useDependencies from './useDependencies';

/**
 * Wrapper around useDependencies which enforces that the vendoredPath is set.
 * @param {string} locator - The locator of the project revision.
 * @param {string} vendoredPath - The vendored path to filter by.
 * @param {DependencyQueryWithFilters} params - The parameters for the query.
 * @param {boolean} [shouldFetch] - Whether to fetch the dependencies.
 */
export function useVendoredDependencies(
  locator: string,
  vendoredPath: string,
  params: DependencyQueryWithFilters,
  shouldFetch = true,
) {
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const isEnabled = isVendoredDependenciesEnabled && shouldFetch;

  // Memoize to ensure that the query params are not re-created on every render.
  const queryParams = useMemo<DependencyQueryWithFilters>(() => ({
    ...params,
    sources: [DependencySource.Vendored],
    vendoredPath,
  }), [params, vendoredPath]);

  return useDependencies(locator, queryParams, isEnabled);
}
